import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import styles from "../styles/CompanyResourcesPage.module.css";

// You can extend these categories or even make them editable later.
const DEFAULT_CATEGORIES = [
  "Supplier",
  "Contractor",
  "Partner",
  "Service",
  "Tooling",
  "Other",
];

const emptyForm = {
  name: "",
  category: "",
  contactName: "",
  phone: "",
  email: "",
  website: "",
  notes: "",
  tags: "", // comma-separated input; stored as array
};

const CompanyResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // resource object or null
  const [form, setForm] = useState(emptyForm);

  const isMounted = useRef(true);

  // Live subscribe to company resources
  useEffect(() => {
    isMounted.current = true;
    const colRef = collection(db, "companyResources");
    const qRef = query(colRef, orderBy("name", "asc"));
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        if (!isMounted.current) return;
        const items = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setResources(items);
        setLoading(false);
      },
      (err) => {
        console.error("Resources listen error:", err);
        if (isMounted.current) {
          setError("Failed to load resources.");
          setLoading(false);
        }
      }
    );
    return () => {
      isMounted.current = false;
      unsub();
    };
  }, []);

  const categoriesFromData = useMemo(() => {
    const set = new Set(DEFAULT_CATEGORIES);
    resources.forEach((r) => r.category && set.add(r.category));
    return ["all", ...Array.from(set)];
  }, [resources]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return resources.filter((r) => {
      const matchesCategory = category === "all" || r.category === category;
      if (!s) return matchesCategory;
      const tagText = Array.isArray(r.tags) ? r.tags.join(", ") : "";
      const hay =
        `${r.name} ${r.category} ${r.contactName} ${r.phone} ${r.email} ${r.website} ${r.notes} ${tagText}`.toLowerCase();
      return matchesCategory && hay.includes(s);
    });
  }, [resources, search, category]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
    setError("");
  };

  const openEdit = (res) => {
    setEditing(res);
    setForm({
      name: res.name || "",
      category: res.category || "",
      contactName: res.contactName || "",
      phone: res.phone || "",
      email: res.email || "",
      website: res.website || "",
      notes: res.notes || "",
      tags: Array.isArray(res.tags) ? res.tags.join(", ") : (res.tags || ""),
    });
    setShowModal(true);
    setError("");
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const normalizeTags = (str) =>
    str
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving) return;

    const payload = {
      name: form.name.trim(),
      category: form.category || "Other",
      contactName: form.contactName.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      website: form.website.trim(),
      notes: form.notes.trim(),
      tags: normalizeTags(form.tags || ""),
      updatedAt: serverTimestamp(),
    };
    if (!payload.name) {
      setError("Name is required.");
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await updateDoc(doc(db, "companyResources", editing.id), payload);
      } else {
        await addDoc(collection(db, "companyResources"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      closeModal();
    } catch (err) {
      console.error("Save resource error:", err);
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (res) => {
    if (!window.confirm(`Delete "${res.name}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, "companyResources", res.id));
    } catch (err) {
      console.error("Delete resource error:", err);
      setError("Failed to delete resource.");
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <h1 className={styles.title}>Company Resources</h1>
        <button className={styles.addBtn} onClick={openCreate}>
          + Add Resource
        </button>
      </div>

      <div className={styles.toolsBar}>
        <div className={styles.searchWrap}>
          <label htmlFor="res-search" className={styles.visuallyHidden}>
            Search resources
          </label>
          <input
            id="res-search"
            type="search"
            placeholder="Search by name, contact, tag, etc."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filtersWrap}>
          <label htmlFor="res-cat" className={styles.filterLabel}>
            Category
          </label>
          <select
            id="res-cat"
            className={styles.select}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {categoriesFromData.map((c) => (
              <option key={c} value={c}>
                {c === "all" ? "All" : c}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {loading ? (
        <div className={styles.loading}>Loading resources…</div>
      ) : filtered.length === 0 ? (
        <div className={styles.empty}>No resources found.</div>
      ) : (
        <div className={styles.grid}>
          {filtered.map((r) => (
            <article className={styles.card} key={r.id}>
              <div className={styles.cardHeader}>
                <h3 className={styles.cardTitle}>{r.name}</h3>
                {r.category && (
                  <span className={styles.badge}>{r.category}</span>
                )}
              </div>

              <dl className={styles.metaList}>
                {r.contactName && (
                  <>
                    <dt>Contact</dt>
                    <dd>{r.contactName}</dd>
                  </>
                )}
                {r.phone && (
                  <>
                    <dt>Phone</dt>
                    <dd>
                      <a href={`tel:${r.phone}`}>{r.phone}</a>
                    </dd>
                  </>
                )}
                {r.email && (
                  <>
                    <dt>Email</dt>
                    <dd>
                      <a href={`mailto:${r.email}`}>{r.email}</a>
                    </dd>
                  </>
                )}
                {r.website && (
                  <>
                    <dt>Website</dt>
                    <dd>
                      <a href={r.website} target="_blank" rel="noreferrer">
                        {r.website}
                      </a>
                    </dd>
                  </>
                )}
              </dl>

              {Array.isArray(r.tags) && r.tags.length > 0 && (
                <div className={styles.tagsRow}>
                  {r.tags.map((t, i) => (
                    <span className={styles.tag} key={`${r.id}-tag-${i}`}>
                      {t}
                    </span>
                  ))}
                </div>
              )}

              {r.notes && <p className={styles.notes}>{r.notes}</p>}

              <div className={styles.cardActions}>
                <button
                  className={styles.actionBtn}
                  onClick={() => openEdit(r)}
                >
                  Edit
                </button>
                <button
                  className={`${styles.actionBtn} ${styles.danger}`}
                  onClick={() => handleDelete(r)}
                >
                  Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {showModal && (
        <div className={styles.modalBackdrop} onMouseDown={closeModal}>
          <div
            className={styles.modal}
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className={styles.modalHeader}>
              <h2>{editing ? "Edit Resource" : "Add Resource"}</h2>
            </div>
            <form className={styles.form} onSubmit={handleSave}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label htmlFor="name">Name *</label>
                  <input
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="category">Category</label>
                  <select
                    id="category"
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                  >
                    <option value="">Select…</option>
                    {DEFAULT_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="contactName">Contact Name</label>
                  <input
                    id="contactName"
                    name="contactName"
                    value={form.contactName}
                    onChange={handleChange}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="phone">Phone</label>
                  <input
                    id="phone"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    value={form.website}
                    onChange={handleChange}
                    placeholder="https://…"
                  />
                </div>

                <div className={`${styles.formGroup} ${styles.colSpanFull}`}>
                  <label htmlFor="tags">Tags (comma separated)</label>
                  <input
                    id="tags"
                    name="tags"
                    value={form.tags}
                    onChange={handleChange}
                    placeholder="materials, rush, wholesale"
                  />
                </div>

                <div className={`${styles.formGroup} ${styles.colSpanFull}`}>
                  <label htmlFor="notes">Notes</label>
                  <textarea
                    id="notes"
                    name="notes"
                    rows={4}
                    value={form.notes}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {error && <div className={styles.error}>{error}</div>}

              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.primaryBtn} disabled={saving}>
                  {saving ? "Saving…" : editing ? "Save Changes" : "Create Resource"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyResourcesPage;
